import { jsonBody, requestJson, requestWithoutContent, withQuery } from "../http/httpClient";
import { readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readString } from "../http/jsonDecoding";
import { readStringList } from "./contentDecoders";
import { readCandidateListing, readUnitsAndMarks, type CandidateListing, type UnitsAndMarks } from "./curationApi";
import { readReviewListEntry, type ReviewListEntry } from "./reviewDecoders";

/**
 * Working together in review (`docs/components/curation-ui.md` CU-9, CU-10, Q-124): the
 * inbox's sections, presence on an open review, review requests ("ask someone to look"),
 * and the bulk decisions that are safe. Every write names the head the curator saw.
 */

const REQUEST_STATES = ["open", "done", "declined", "withdrawn"] as const;
export type ReviewRequestState = (typeof REQUEST_STATES)[number];

export interface ReviewRequest {
  readonly requestId: string;
  readonly changeSetId: string;
  readonly askedBy: string;
  readonly asked: string;
  /** Empty: the whole change set. */
  readonly unitKeys: readonly string[];
  readonly note: string;
  readonly state: ReviewRequestState;
  readonly at: string;
  readonly history: readonly { readonly state: ReviewRequestState; readonly actor: string; readonly at: string; readonly reason: string }[];
}

export interface InboxEntry {
  readonly review: ReviewListEntry;
  /** I curate a touched item and have not approved the shown head. */
  readonly toApprove: boolean;
  /** Waiting for me: open requests to me. My proposals: my open requests to others. */
  readonly requests: readonly ReviewRequest[];
  /** Possible duplicates that need a resolution before the merge. */
  readonly openCandidateCount: number;
  readonly sentBack: { readonly actor: string; readonly at: string; readonly reason: string } | null;
}

// Recently merged is no inbox section: the sidebar is the inbox, and merged work is settled (F108).
export const INBOX_SECTIONS = ["waitingForMe", "sentBackToMe", "myOpenProposals"] as const;
export type InboxSection = (typeof INBOX_SECTIONS)[number];
export interface KnowledgeInboxEntry {
  readonly source: string;
  readonly iri: string;
  readonly document: string;
  readonly statements: number;
  readonly by: string;
  readonly recordedAt: string;
  readonly newerPendingCount: number;
}
export type Inbox = Readonly<Record<InboxSection, readonly InboxEntry[]>> & {
  readonly knowledgeData: { readonly waitingForMe: readonly KnowledgeInboxEntry[]; readonly myProposals: readonly KnowledgeInboxEntry[] };
};

export interface PresentPerson {
  readonly userId: string;
  readonly unitKey: string | null;
  readonly since: string;
}

export interface ReviewPresence {
  /** The head now; the page shows "reload" when it is not the one shown. */
  readonly head: string;
  /** Changes with every decision, mark, candidate proposal or request. */
  readonly activity: string;
  readonly present: readonly PresentPerson[];
}

export function readReviewRequest(value: unknown): ReviewRequest {
  const body = readObject(value, "review request");
  return {
    requestId: readString(body, "request_id"),
    changeSetId: readString(body, "change_set_id"),
    askedBy: readString(body, "asked_by"),
    asked: readString(body, "asked"),
    unitKeys: readStringList(body, "unit_keys"),
    note: readString(body, "note"),
    state: readOneOf(body, "state", REQUEST_STATES),
    at: readString(body, "at"),
    history: readArray(body["history"], "request history", (item) => {
      const change = readObject(item, "request state change");
      return {
        state: readOneOf(change, "state", REQUEST_STATES),
        actor: readString(change, "actor"),
        at: readString(change, "at"),
        reason: readString(change, "reason"),
      };
    }),
  };
}

function readInboxEntry(value: unknown): InboxEntry {
  const body = readObject(value, "inbox entry");
  const sentBack = body["sent_back"] === null ? null : readObject(body["sent_back"], "sent back");
  return {
    review: readReviewListEntry(body["review"]),
    toApprove: readBoolean(body, "to_approve"),
    requests: readArray(body["requests"], "requests", readReviewRequest),
    openCandidateCount: readInteger(body, "open_candidate_count"),
    sentBack: sentBack === null ? null : { actor: readString(sentBack, "actor"), at: readString(sentBack, "at"), reason: readString(sentBack, "reason") },
  };
}

export function readInbox(value: unknown): Inbox {
  const body = readObject(value, "inbox");
  const knowledge = readObject(body["knowledge_data"], "knowledge inbox");
  const knowledgeSection = (key: string) => readArray(knowledge[key], key, (value) => {
    const entry = readObject(value, "knowledge proposal");
    return { source: readString(entry, "source"), iri: readString(entry, "iri"), document: readString(entry, "document"), statements: readInteger(entry, "statements"), by: readString(entry, "by"), recordedAt: readString(entry, "recorded_at"), newerPendingCount: readInteger(entry, "newer_pending_count") };
  });
  const section = (key: string) => readArray(body[key], key, readInboxEntry);
  return {
    knowledgeData: { waitingForMe: knowledgeSection("waiting_for_me"), myProposals: knowledgeSection("my_proposals") },
    waitingForMe: section("waiting_for_me"),
    sentBackToMe: section("sent_back_to_me"),
    myOpenProposals: section("my_open_proposals"),
  };
}

export function readReviewPresence(value: unknown): ReviewPresence {
  const body = readObject(value, "presence");
  return {
    head: readString(body, "head"),
    activity: readString(body, "activity"),
    present: readArray(body["present"], "present", (item) => {
      const person = readObject(item, "present person");
      return { userId: readString(person, "user_id"), unitKey: readNullableString(person, "unit_key"), since: readString(person, "since") };
    }),
  };
}

const changeSetPath = (changeSetId: string): `/${string}` => `/content/change-sets/${encodeURIComponent(changeSetId)}`;
const requestPath = (requestId: string): `/${string}` => `/content/review-requests/${encodeURIComponent(requestId)}`;

export function getInbox(projectId: string, signal?: AbortSignal): Promise<Inbox> {
  return requestJson({ method: "GET", path: withQuery(`/projects/${encodeURIComponent(projectId)}/reviews/inbox`, { limit: 50 }), signal, decodeResponse: readInbox });
}

/** The heartbeat of an open review: who is here, the head, and an activity stamp. */
export function beatPresence(changeSetId: string, unitKey: string | null, signal?: AbortSignal): Promise<ReviewPresence> {
  return requestJson({ method: "PUT", path: `${changeSetPath(changeSetId)}/presence`, body: jsonBody({ unit_key: unitKey }), signal, decodeResponse: readReviewPresence });
}

export function leaveReview(changeSetId: string): Promise<void> {
  return requestWithoutContent({ method: "DELETE", path: `${changeSetPath(changeSetId)}/presence` });
}

export function listReviewRequests(changeSetId: string, signal?: AbortSignal): Promise<readonly ReviewRequest[]> {
  return requestJson({
    method: "GET",
    path: `${changeSetPath(changeSetId)}/review-requests`,
    signal,
    decodeResponse: (body) => readArray(body, "review requests", readReviewRequest),
  });
}

export function askForReview(changeSetId: string, request: { readonly asked: string; readonly unitKeys: readonly string[]; readonly note: string }): Promise<ReviewRequest> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/review-requests`,
    body: jsonBody({ asked: request.asked, unit_keys: [...request.unitKeys], note: request.note }),
    decodeResponse: readReviewRequest,
  });
}

export function declineReviewRequest(requestId: string, reason: string): Promise<ReviewRequest> {
  return requestJson({ method: "POST", path: `${requestPath(requestId)}/decline`, body: jsonBody({ reason }), decodeResponse: readReviewRequest });
}

export function withdrawReviewRequest(requestId: string): Promise<ReviewRequest> {
  return requestJson({ method: "POST", path: `${requestPath(requestId)}/withdraw`, decodeResponse: readReviewRequest });
}

/** Mark the units OK at `head`; the server refuses the whole selection if one is not safe. */
export function markUnitsOk(changeSetId: string, head: string, unitKeys: readonly string[]): Promise<UnitsAndMarks> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/marks/bulk-ok`,
    body: jsonBody({ head, unit_keys: [...unitKeys] }),
    decodeResponse: readUnitsAndMarks,
  });
}

/** Keep the new terms of low-scored proposals: one resolution each, no commit. */
export function keepNewTerms(changeSetId: string, expectedHead: string, candidateIds: readonly string[], reason: string): Promise<CandidateListing> {
  return requestJson({
    method: "POST",
    path: `${changeSetPath(changeSetId)}/candidates/bulk-keep`,
    body: jsonBody({ expected_head: expectedHead, candidate_ids: [...candidateIds], reason }),
    decodeResponse: readCandidateListing,
  });
}

